import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import * as monthUtils from '@actual-app/core/shared/months';

import { cor, tela, texto } from '#components/finapp/estilo';
import type { LancamentoPendente } from '#components/finapp/motor/filaPendencias';
import { usePendencias } from '#components/finapp/motor/usePendencias';
import { Page } from '#components/Page';
import { useNavigate } from '#hooks/useNavigate';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { PrecisaAgora } from './PrecisaAgora';
import { ResumoDaFila } from './ResumoDaFila';
import { SemCategoria } from './SemCategoria';

/**
 * Quantos lançamentos sem categoria a tela desenha de uma vez.
 *
 * ⚠️ A primeira sincronização do Open Finance trouxe **1.125 lançamentos de
 * 10 contas**. Desenhar tudo de uma vez trava o navegador e faz a fila
 * parecer intransponível logo no primeiro olhar — o oposto do que ela
 * deveria provocar. O número da fila continua sendo o TOTAL (o contador não
 * mente); só o desenho é que vem por partes.
 */
const POR_PAGINA = 25;

/**
 * Tela **Pendências** (`/pendencias`) — a fila do que precisa de atenção.
 *
 * ⚠️⚠️ POR QUE ELA EXISTE: a 2ª queixa do dono foi "como corrigir um
 * lançamento errado?", e hoje não há caminho nenhum — ele teria que abrir
 * conta por conta caçando o que veio sem categoria. Esta tela é a resposta:
 * duas faixas, **Precisa agora** (banco desconectado · conta fixa vencida) e
 * **Sem categoria** (o que o banco trouxe e ninguém classificou).
 *
 * A tela é só a FIAÇÃO: `motor/filaPendencias.ts` decide o que entra na fila
 * (puro e testado), `motor/usePendencias.ts` busca, e os três componentes
 * abaixo só desenham. O que mora aqui é o que precisa de React e de redux —
 * o gesto de classificar e o aprendizado.
 *
 * ⛔ Esta tela NÃO registra a própria rota nem mexe no menu: `FinancesApp.tsx`
 * e a barra lateral são fiados em paralelo. O contrato com quem faz essa
 * fiação é o export `Pendencias` (aqui) e `useContadorPendencias`
 * (`motor/usePendencias.ts`), pro badge do menu.
 */
export function Pendencias() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { fila, carregando, erro, recarregar } = usePendencias();

  const [quantosMostrar, setQuantosMostrar] = useState(POR_PAGINA);
  const [alternados, setAlternados] = useState<ReadonlySet<string>>(new Set());

  /**
   * ⭐ A pref NATIVA do Actual (`learn-categories`, ligada por padrão, a
   * mesma que a tela de Quem recebe respeita) decide o ESTADO INICIAL da
   * caixa — não um `true` nosso cravado no código. Se o dono já desligou o
   * aprendizado nos ajustes, esta tela não pode reabilitá-lo por baixo dos
   * panos.
   */
  const [learnCategoriesPref = 'true'] = useSyncedPref('learn-categories');
  const aprenderPorPadrao = String(learnCategoriesPref) === 'true';

  /**
   * `alternados` guarda as EXCEÇÕES (o que o dono clicou), não o estado
   * absoluto — assim a lista pode crescer/recarregar sem perder a escolha
   * dele e sem precisar semear um valor por item novo que chega.
   */
  const aprendizadoDesligado = useMemo(() => {
    if (aprenderPorPadrao) return alternados;
    return new Set(
      fila.semCategoria.map(i => i.id).filter(id => !alternados.has(id)),
    );
  }, [aprenderPorPadrao, alternados, fila.semCategoria]);

  function alternarAprendizado(id: string) {
    setAlternados(atual => {
      const proximo = new Set(atual);
      if (proximo.has(id)) {
        proximo.delete(id);
      } else {
        proximo.add(id);
      }
      return proximo;
    });
  }

  /**
   * ⭐ O GESTO (spec §5.2): abre o modal NATIVO `'category-autocomplete'` —
   * o mesmo de outras 7 telas do app. Um dispatch, zero componente novo.
   *
   * ⭐⭐ E O PARÂMETRO QUE DECIDE TUDO (spec §5.3):
   * `learnCategories: true` em `transactions-batch-update`. É ele que faz o
   * Actual (`transaction-rules.ts`) olhar as 5 últimas transações do mesmo
   * estabelecimento e, com 3 iguais, criar/atualizar a regra sozinho. O
   * import de banco NÃO passa esse flag (`sync.ts`, default `false`) — sem
   * passarmos aqui de propósito, o app não aprende nada e a fila vira
   * trabalho eterno.
   *
   * O valor vem da CAIXA do lançamento (`aprendizadoDesligado`), nunca de um
   * `true` fixo: o dono precisa poder classificar um caso isolado sem virar
   * regra (risco R5).
   */
  function classificar(item: LancamentoPendente) {
    const aprender = !aprendizadoDesligado.has(item.id);

    dispatch(
      pushModal({
        modal: {
          name: 'category-autocomplete',
          options: {
            title: item.descricao,
            month: monthUtils.monthFromDate(item.data),
            onSelect: (categoryId: string | null) => {
              if (!categoryId) return;
              void send('transactions-batch-update', {
                updated: [{ id: item.id, category: categoryId }],
                learnCategories: aprender,
              }).then(() => {
                // Sem isto o lançamento ficaria na lista até um F5 e o dono
                // classificaria o mesmo item duas vezes achando que o
                // primeiro toque não pegou.
                recarregar();
              });
            },
          },
        },
      }),
    );
  }

  const visiveis = fila.semCategoria.slice(0, quantosMostrar);
  const restantes = fila.semCategoria.length - visiveis.length;

  return (
    <Page header={t('Pendências')}>
      <View style={tela}>
        {/*
         * ⚠️ A ORDEM dos estados importa, mesma régua de
         * `inicio/ContasDoMes.tsx`: erro vence tudo. Numa tela cuja fila
         * vazia significa "está tudo em dia", mostrar o estado saudável
         * porque a consulta quebrou seria a pior mentira possível.
         */}
        {erro ? (
          <Text data-testid="pendencias-erro" style={{ color: cor.saida }}>
            {t('Não consegui montar a fila: {{erro}}', { erro: erro.message })}
          </Text>
        ) : carregando && fila.total === 0 ? (
          <Text data-testid="pendencias-carregando" style={texto.corpo}>
            <Trans>Carregando…</Trans>
          </Text>
        ) : (
          <>
            <ResumoDaFila
              total={fila.total}
              totalSemCategoria={fila.totalSemCategoria}
            />
            <PrecisaAgora
              itens={fila.precisaAgora}
              onAbrir={item => {
                if (item.destino) void navigate(item.destino);
              }}
            />
            <SemCategoria
              itens={visiveis}
              restantes={restantes}
              onMostrarMais={() => setQuantosMostrar(n => n + POR_PAGINA)}
              aprendizadoDesligado={aprendizadoDesligado}
              onAlternarAprendizado={alternarAprendizado}
              onClassificar={classificar}
            />
          </>
        )}
      </View>
    </Page>
  );
}
